import { IconButton } from '@atlaskit/button/new';
import CloseIcon from '@atlaskit/icon/core/close';
import { Flex } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { DatabaseFieldType, Hyperlink } from '@databases/types/database';
import { MultiValueChangeType } from '@databases/utils/database/changesets';
import React, { FunctionComponent, useCallback } from 'react';

import { EditComponentProps } from '../types';

interface CurrentDataSectionValueProps
  extends EditComponentProps<DatabaseFieldType.HYPERLINK> {
  link: Hyperlink & {
    _id: string;
  };
  setHasCurrentData: React.Dispatch<React.SetStateAction<boolean>>;
  isMulti: boolean;
  multiAddDataSectionRef: React.MutableRefObject<HTMLTextAreaElement | null>;
}

export const CurrentDataSectionValue: FunctionComponent<CurrentDataSectionValueProps> = ({
  onChange,
  onBlur,
  autoFocus,
  value: linkValues,
  link,
  setHasCurrentData,
  isMulti,
  multiAddDataSectionRef,
}) => {
  const handleCurrentDataSectionBlur = useCallback(() => {
    setHasCurrentData(
      linkValues.length > 0 && linkValues.some((link) => link.url !== '')
    );
    onBlur?.();
  }, [linkValues, onBlur, setHasCurrentData]);

  const linkValueIndex = linkValues.findIndex(
    (value) => link._id !== '' && value._id === link._id
  );

  const onItemDelete = useCallback(() => {
    onChange(linkValues, {
      type: DatabaseFieldType.HYPERLINK,
      delta: {
        type: MultiValueChangeType.REMOVE,
        index: linkValueIndex,
      },
    });

    if (isMulti) {
      setHasCurrentData(
        linkValues.length > 0 && linkValues.some((link) => link.url !== '')
      );
    } else {
      setHasCurrentData(false);
    }

    multiAddDataSectionRef.current?.focus();
  }, [
    onChange,
    linkValues,
    linkValueIndex,
    isMulti,
    multiAddDataSectionRef,
    setHasCurrentData,
  ]);

  return (
    <Flex key={link._id} alignItems={'center'}>
      <div
        data-testid="hyperlink-popover-value"
        style={{
          border: `1px solid ${token('color.border')}`,
          borderRadius: '3px',
          paddingLeft: token('space.050', '4px'),
          paddingRight: token('space.050', '4px'),
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        {link.url}
      </div>
      <IconButton
        testId="hyperlink-popover-value-remove"
        data-popover-edit-focus
        appearance="subtle"
        icon={CloseIcon}
        label=""
        onBlur={handleCurrentDataSectionBlur}
        onClick={onItemDelete}
        autoFocus={autoFocus}
      />
    </Flex>
  );
};
